import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Trash2, User2 } from "lucide-react";

const KIND_LABEL = { ante_mortem: "Missing person", post_mortem: "Unidentified body" };
const KIND_STYLE = {
  ante_mortem: "border-[#3B6EA5]/30 bg-[#3B6EA5]/10 text-[#3B6EA5]",
  post_mortem: "border-[#4A7C59]/30 bg-[#4A7C59]/10 text-[#4A7C59]",
};
const SOURCE_LABEL = { single: "Single delete", bulk: "Bulk delete", account_cascade: "Account removal" };

export default function AdminDeletionLogsPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/deletion-logs")
      .then((r) => setLogs(r.data))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="mx-auto max-w-5xl px-6 py-10" data-testid="admin-deletion-logs-page">
      <p className="font-body text-xs uppercase tracking-[0.18em] text-[#6B7F82]">Admin · audit</p>
      <div className="mt-1 flex items-center gap-3">
        <span className="grid h-9 w-9 place-items-center rounded-md bg-[#9E4747] text-white"><Trash2 size={18} /></span>
        <h1 className="font-heading text-3xl font-semibold text-[#1A2024]">Deletion log</h1>
      </div>
      <p className="mt-2 max-w-2xl font-body text-sm text-[#5C6873]">
        A permanent record of every case removed from the database — who deleted it, when, and how.
      </p>

      {loading && <div className="mt-8 font-body text-sm text-[#5C6873]" data-testid="deletion-logs-loading">Loading…</div>}

      {!loading && logs.length === 0 && (
        <div className="mt-8 rounded-lg border border-dashed border-[#E2E8F0] bg-[#F8F9FA] p-10 text-center font-body text-sm text-[#5C6873]" data-testid="deletion-logs-empty">
          No deletions recorded yet.
        </div>
      )}

      <div className="mt-6 space-y-2" data-testid="deletion-logs-list">
        {logs.map((l) => (
          <div key={l.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[#E2E8F0] bg-white p-4 shadow-sm" data-testid={`deletion-log-${l.id}`}>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline" className={`rounded-full text-[11px] ${KIND_STYLE[l.kind] || ""}`}>
                  {KIND_LABEL[l.kind] || l.kind}
                </Badge>
                <span className="font-mono text-sm font-semibold text-[#1A2024]">{l.reference_code || "—"}</span>
                {l.full_name && <span className="font-body text-sm text-[#5C6873]">{l.full_name}</span>}
                {l.country && <span className="font-body text-xs text-[#6B7F82]">· {l.country}</span>}
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-1.5 font-body text-xs text-[#5C6873]">
                <User2 size={12} /> {l.deleted_by}
                {l.deleted_by_email && <span className="text-[#6B7F82]">({l.deleted_by_email})</span>}
                <span>· {l.deleted_at ? new Date(l.deleted_at).toLocaleString() : ""}</span>
              </div>
            </div>
            <Badge variant="outline" className="rounded-full border-[#E2E8F0] bg-[#F8F9FA] text-[11px] text-[#5C6873]">
              {SOURCE_LABEL[l.source] || l.source}
            </Badge>
          </div>
        ))}
      </div>
    </div>
  );
}
